import type { TimingAnalyticsRow } from "@/types/factory";

export interface FactoryTimingWindow {
  startDate: string;
  endDate: string;
}

function localDate(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

/** Current local calendar month, used consistently by portfolio and project analytics. */
export function defaultFactoryTimingWindow(now = new Date()): FactoryTimingWindow {
  return {
    startDate: localDate(new Date(now.getFullYear(), now.getMonth(), 1)),
    endDate: localDate(now),
  };
}

export interface AnnotatorTimingGroup {
  annotator: string;
  projects: Map<string, TimingAnalyticsRow[]>;
}

/** Keep worklist metrics intact: total timers and averages describe different populations. */
export function groupFactoryTimingRows(rows: TimingAnalyticsRow[]): AnnotatorTimingGroup[] {
  const annotators = new Map<string, AnnotatorTimingGroup>();
  for (const source of rows) {
    const row = annotators.get(source.annotator) ?? {
      annotator: source.annotator,
      projects: new Map<string, TimingAnalyticsRow[]>(),
    };
    const worklists = row.projects.get(source.project_id) ?? [];
    worklists.push(source);
    row.projects.set(source.project_id, worklists);
    annotators.set(source.annotator, row);
  }
  return [...annotators.values()].sort((left, right) =>
    left.annotator.localeCompare(right.annotator),
  );
}

export function timingWorklistLabel(row: TimingAnalyticsRow): string {
  const phase = row.phase == null ? "Phase unspecified" : `Phase ${row.phase}`;
  if (row.is_ground_truth) return `Reference · ${phase}`;
  if (row.phase === 0) return "Training · Phase 0";
  return phase;
}

function csvEscape(value: string | number): string {
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}

/** Export one record per annotator and worklist, preserving the API's measured averages. */
export function buildFactoryTimingCsv(
  rows: TimingAnalyticsRow[],
  startDate: string,
  endDate: string,
): string {
  const header = [
    "Annotator",
    "Project",
    "Project ID",
    "Worklist",
    "Batch",
    "Phase",
    "Reference",
    "Completed Cases",
    "Timed Cases",
    "Timing Coverage",
    "Average Completed Case Time (s)",
    "Annotation Count",
    "Stones",
    "Average Timed Annotation Time (s)",
    "Last Active",
    "Latest Activity From",
    "Latest Activity Through",
    "Timer Basis",
  ];
  const body = groupFactoryTimingRows(rows).flatMap((group) =>
    [...group.projects.values()].flatMap((worklists) =>
      worklists.map((row) => [
        row.annotator,
        row.project_name,
        row.project_id,
        row.worklist_project_id,
        row.batch_id ?? "",
        row.phase ?? "",
        String(row.is_ground_truth),
        row.completed_cases,
        row.timed_cases,
        row.timing_coverage ?? "",
        row.average_case_time_seconds ?? "",
        row.annotation_count,
        row.stone_count,
        row.average_annotation_seconds ?? "",
        row.last_activity ?? "",
        startDate,
        endDate,
        "Cumulative",
      ]),
    ),
  );
  return [header, ...body].map((line) => line.map(csvEscape).join(",")).join("\n");
}
